// src/pages/AbsenceOverview.js
// Uppdaterad så den använder activeCompany och även räknar godkända frånvaroansökningar.

import React, { useEffect, useMemo, useState } from "react";
import LoadingIndicator from "../components/LoadingIndicator";
import { calculateAbsencePay } from "../utils/absencePay";
import {
  FaSearch,
  FaUserSlash,
  FaRegClock,
  FaMoneyBillWave,
  FaUser,
} from "react-icons/fa";
import {
  collection,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import { db } from "../firebase";
import { useCompany } from "../contexts/CompanyContext";

const absenceOverviewCss = `
.absence-person-toggle { display: flex; align-items: center; gap: 8px; width: 100%; border: 0; padding: 0; background: transparent; color: inherit; font: inherit; text-align: left; cursor: pointer; }
.absence-person-toggle:focus-visible, .absence-calculation summary:focus-visible { outline: 2px solid #dc2626; outline-offset: 5px; border-radius: 4px; }
.absence-person-heading { display: flex; flex-wrap: wrap; justify-content: space-between; gap: 8px; font-size: 15px; color: #1f2937; }
.absence-person-heading > span, .absence-muted, .absence-help, .absence-no-compensation { color: #5f6b7a; }
.absence-help { margin: 8px 0 16px; font-size: 13px; line-height: 1.6; }
.absence-shift-list { display: grid; gap: 12px; }
.absence-shift-card { background: white; border: 1px solid #dfe5ee; border-radius: 10px; overflow: hidden; }
.absence-shift-summary { display: grid; grid-template-columns: minmax(150px, 1.4fr) 1fr 1fr 1fr; align-items: center; gap: 16px; padding: 16px; font-size: 14px; color: #1f2937; }
.absence-muted { display: block; font-size: 12px; margin-bottom: 3px; }
.absence-shift-amount { text-align: right; }
.absence-shift-amount strong { color: #b91c1c; font-size: 17px; }
.absence-calculation { border-top: 1px solid #edf0f5; }
.absence-calculation summary { padding: 10px 16px; cursor: pointer; color: #b91c1c; font-weight: 600; }
.absence-hide, .absence-calculation[open] .absence-show { display: none; }
.absence-calculation[open] .absence-hide { display: inline; }
.absence-calculation-scroll { overflow-x: auto; padding: 0 16px; }
.absence-calculation-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.absence-calculation-table caption { text-align: left; padding: 8px 0; font-weight: 600; }
.absence-calculation-table th, .absence-calculation-table td { padding: 10px 12px; text-align: left; border-bottom: 1px solid #edf0f5; }
.absence-calculation-table thead { background: #f4f6fa; color: #526075; }
.absence-calculation-table td { white-space: nowrap; font-variant-numeric: tabular-nums; }
.absence-calculation-table td:last-child, .absence-calculation-table thead th:last-child { text-align: right; }
.absence-calculation-table tfoot { font-weight: 700; color: #1f2937; }
.absence-calculation > .absence-help { padding: 0 16px; }
.absence-no-compensation { margin: 0; padding: 10px 16px; border-top: 1px solid #edf0f5; font-size: 13px; }
@media (max-width: 700px) {
  .absence-shift-summary { grid-template-columns: 1fr 1fr; gap: 12px; }
  .absence-shift-amount { text-align: left; }
}
`;

function safeParseDate(dateValue) {
  if (!dateValue) return null;

  try {
    if (dateValue instanceof Date) {
      return isNaN(dateValue.getTime()) ? null : dateValue;
    }

    if (typeof dateValue === "object" && typeof dateValue.toDate === "function") {
      const parsed = dateValue.toDate();
      return parsed instanceof Date && !isNaN(parsed.getTime()) ? parsed : null;
    }

    if (typeof dateValue === "string" || typeof dateValue === "number") {
      const parsed = new Date(dateValue);
      return isNaN(parsed.getTime()) ? null : parsed;
    }

    return null;
  } catch (error) {
    console.error("Fel vid datumtolkning:", error);
    return null;
  }
}

function getMonthIndexFromSwedish(monthName) {
  const months = {
    Januari: 0,
    Februari: 1,
    Mars: 2,
    April: 3,
    Maj: 4,
    Juni: 5,
    Juli: 6,
    Augusti: 7,
    September: 8,
    Oktober: 9,
    November: 10,
    December: 11,
  };

  return months[monthName] ?? 0;
}

function formatTimeDisplay(timeString) {
  if (!timeString) return "N/A";
  return String(timeString).substring(0, 5);
}

function formatDateDisplay(date) {
  const d = safeParseDate(date);
  if (!d) return "N/A";
  return d.toLocaleDateString("sv-SE");
}

function timeToMinutes(timeString) {
  if (!timeString || typeof timeString !== "string") return 0;

  const [h, m] = timeString.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return 0;
  return h * 60 + m;
}

function calculateTotalShiftMinutes(shift) {
  if (!shift?.startTime || !shift?.endTime) return 0;

  const startMinutes = timeToMinutes(String(shift.startTime));
  const endMinutes = timeToMinutes(String(shift.endTime));

  let totalMinutes = endMinutes - startMinutes;
  if (totalMinutes < 0) totalMinutes += 24 * 60;

  return totalMinutes;
}

function calculateTotalBreakMinutes(employeeData) {
  let totalBreakMinutes = 0;

  if (employeeData?.breaks && Array.isArray(employeeData.breaks)) {
    employeeData.breaks.forEach((breakItem) => {
      if (breakItem.appliesToWholeShift) return;

      if (breakItem.startTime && breakItem.endTime) {
        const start = timeToMinutes(breakItem.startTime);
        const end = timeToMinutes(breakItem.endTime);

        let breakMinutes = end - start;
        if (breakMinutes < 0) breakMinutes += 24 * 60;

        totalBreakMinutes += breakMinutes;
      }
    });
  }

  return totalBreakMinutes;
}

function calculateShiftHours(shift, employeeData = null) {
  if (!shift?.startTime || !shift?.endTime) return 0;

  try {
    const totalMinutes = calculateTotalShiftMinutes(shift);
    let breakMinutes = 0;

    if (employeeData) {
      breakMinutes = calculateTotalBreakMinutes(employeeData);

      if (employeeData.breaks && Array.isArray(employeeData.breaks)) {
        employeeData.breaks.forEach((breakItem) => {
          if (breakItem.appliesToWholeShift) {
            const totalHours = totalMinutes / 60;

            if (totalHours >= 5 && totalHours < 8) {
              breakMinutes = Math.max(breakMinutes, 30);
            } else if (totalHours >= 8) {
              breakMinutes = Math.max(breakMinutes, 60);
            }
          }
        });
      }
    } else {
      breakMinutes = Number(shift.breakMinutes || shift.break || 0);
    }

    let workedMinutes = Math.max(0, totalMinutes - breakMinutes);

    if (employeeData?.absences && Array.isArray(employeeData.absences)) {
      employeeData.absences.forEach((absenceItem) => {
        let absenceMinutes = 0;

        if (absenceItem.appliesToWholeShift) {
          absenceMinutes = workedMinutes;
        } else if (absenceItem.startTime && absenceItem.endTime) {
          const start = timeToMinutes(absenceItem.startTime);
          const end = timeToMinutes(absenceItem.endTime);

          let minutes = end - start;
          if (minutes < 0) minutes += 24 * 60;

          absenceMinutes = minutes;
        } else if (Number(absenceItem.hours || 0) > 0) {
          absenceMinutes = Number(absenceItem.hours) * 60;
        }

        workedMinutes = Math.max(0, workedMinutes - absenceMinutes);
      });
    }

    return workedMinutes / 60;
  } catch (error) {
    console.error("Fel vid beräkning av passets timmar:", error);
    return 0;
  }
}

function calculateAbsenceForShift(shift, employeeData = null) {
  if (!shift?.startTime || !shift?.endTime) {
    return { hours: 0, value: 0, details: [] };
  }

  try {
    let absenceMinutes = 0;
    let absenceValue = 0;
    const absenceDetails = [];

    if (employeeData?.absences && Array.isArray(employeeData.absences)) {
      employeeData.absences.forEach((absenceItem) => {
        let itemMinutes = 0;
        let startTimeFormatted = "";
        let endTimeFormatted = "";

        if (absenceItem.appliesToWholeShift) {
          const totalMinutes = calculateTotalShiftMinutes(shift);
          const breakMinutes = calculateTotalBreakMinutes(employeeData);
          itemMinutes = Math.max(0, totalMinutes - breakMinutes);
          startTimeFormatted = formatTimeDisplay(shift.startTime);
          endTimeFormatted = formatTimeDisplay(shift.endTime);
        } else if (absenceItem.startTime && absenceItem.endTime) {
          const start = timeToMinutes(absenceItem.startTime);
          const end = timeToMinutes(absenceItem.endTime);

          let minutes = end - start;
          if (minutes < 0) minutes += 24 * 60;

          itemMinutes = minutes;
          startTimeFormatted = formatTimeDisplay(absenceItem.startTime);
          endTimeFormatted = formatTimeDisplay(absenceItem.endTime);
        } else if (Number(absenceItem.hours || 0) > 0) {
          itemMinutes = Number(absenceItem.hours) * 60;
        }

        absenceMinutes += itemMinutes;

        const salaryPercent = Number(
          absenceItem.absencePercent ?? absenceItem.salaryPercent ?? 0
        );
        const deductionPercent = (100 - salaryPercent) / 100;
        const itemValue = (itemMinutes / 60) * deductionPercent;

        absenceValue += itemValue;

        absenceDetails.push({
          type: absenceItem.absenceTypeName || absenceItem.type || "Frånvaro",
          absencePercent: salaryPercent,
          hours: itemMinutes / 60,
          value: itemValue,
          startTime: startTimeFormatted,
          endTime: endTimeFormatted,
          appliesToWholeShift: Boolean(absenceItem.appliesToWholeShift),
          approvedFromRequest: Boolean(absenceItem.approvedFromRequest),
          absenceRequestId: absenceItem.absenceRequestId || null,
        });
      });
    }

    return {
      hours: absenceMinutes / 60,
      value: absenceValue,
      details: absenceDetails,
    };
  } catch (error) {
    console.error("Fel vid beräkning av frånvaro:", error);
    return { hours: 0, value: 0, details: [] };
  }
}

const formatHours = (hours) =>
  Number(hours || 0).toLocaleString("sv-SE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatCurrency = (amount) =>
  `${Number(amount || 0).toLocaleString("sv-SE", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })} kr`;

function getEmployeeIdFromAbsenceRequest(request, employeeList = []) {
  if (!request) return null;

  if (request.employeeId) return request.employeeId;
  if (request.userId) return request.userId;
  if (request.uid) return request.uid;

  const email = String(request.userEmail || request.email || "")
    .trim()
    .toLowerCase();

  if (!email) return null;

  const employee = employeeList.find(
    (emp) => String(emp.email || "").trim().toLowerCase() === email
  );

  return employee?.id || null;
}

function buildApprovedAbsenceEntry(request, absenceTypes = []) {
  const matchingType = request?.absenceTypeId
    ? absenceTypes.find((type) => type.id === request.absenceTypeId)
    : null;

  return {
    absenceRequestId: request.id,
    startTime: request.startTime || "",
    endTime: request.endTime || "",
    appliesToWholeShift: Boolean(request.appliesToWholeShift),
    absenceTypeId: request.absenceTypeId || matchingType?.id || "approved-request",
    absenceTypeName:
      request.absenceTypeName || matchingType?.name || "Godkänd frånvaro",
    type: request.absenceTypeName || matchingType?.name || "Godkänd frånvaro",
    absencePercent: Number(
      request.absencePercent ??
        request.salaryPercent ??
        matchingType?.salaryPercent ??
        0
    ),
    hours: Number(request.hours || 0),
    approvedFromRequest: true,
  };
}

function mergeApprovedAbsenceForEmployee({
  shift,
  employeeId,
  employeeData,
  approvedAbsenceRequests,
  employees,
  absenceTypes,
}) {
  const shiftId = shift?.originalId || shift?.id;
  if (!shiftId || !employeeId) return employeeData;

  const currentAbsences = Array.isArray(employeeData?.absences)
    ? [...employeeData.absences]
    : [];

  const existingRequestIds = new Set(
    currentAbsences.map((entry) => entry?.absenceRequestId).filter(Boolean)
  );

  const matchingRequests = (approvedAbsenceRequests || []).filter((request) => {
    const requestShiftId = request?.shiftId || request?.originalShiftId;
    if (requestShiftId !== shiftId) return false;

    const requestEmployeeId = getEmployeeIdFromAbsenceRequest(request, employees);
    return requestEmployeeId === employeeId;
  });

  const requestAbsenceEntries = matchingRequests
    .filter((request) => !existingRequestIds.has(request.id))
    .map((request) => buildApprovedAbsenceEntry(request, absenceTypes));

  if (requestAbsenceEntries.length === 0) return employeeData;

  return {
    ...employeeData,
    absences: [...currentAbsences, ...requestAbsenceEntries],
  };
}

function getEmployeeName(employee) {
  if (!employee) return "Okänd anställd";

  if (employee.name) return employee.name;
  if (employee.firstName && employee.lastName) {
    return `${employee.firstName} ${employee.lastName}`;
  }
  if (employee.firstName) return employee.firstName;
  if (employee.lastName) return employee.lastName;
  if (employee.displayName) return employee.displayName;
  if (employee.userName) return employee.userName;

  return "Okänd anställd";
}

export default function AbsenceOverview() {
  const { activeCompany, loading: companyLoading } = useCompany();

  const [shifts, setShifts] = useState([]);
  const [absenceTypes, setAbsenceTypes] = useState([]);
  const [approvedAbsenceRequests, setApprovedAbsenceRequests] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [expandedEmployee, setExpandedEmployee] = useState(null);

  const companyRootUid = activeCompany?.companyAccountUid;

  useEffect(() => {
    const currentDate = new Date();
    const months = [
      "Januari",
      "Februari",
      "Mars",
      "April",
      "Maj",
      "Juni",
      "Juli",
      "Augusti",
      "September",
      "Oktober",
      "November",
      "December",
    ];

    setSelectedMonth(months[currentDate.getMonth()]);
    setSelectedYear(currentDate.getFullYear().toString());
  }, []);

  const monthsInSwedish = [
    "Januari",
    "Februari",
    "Mars",
    "April",
    "Maj",
    "Juni",
    "Juli",
    "Augusti",
    "September",
    "Oktober",
    "November",
    "December",
  ];

  const years = useMemo(() => {
    const currentYearNum = new Date().getFullYear();
    const list = [];

    for (let year = 2020; year <= currentYearNum + 2; year++) {
      list.push(year.toString());
    }

    return list;
  }, []);

  useEffect(() => {
    if (!companyRootUid) {
      setEmployees([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    const employedRef = collection(db, "users", companyRootUid, "employed");

    const unsubscribe = onSnapshot(
      employedRef,
      (snapshot) => {
        const users = [];

        snapshot.forEach((userDoc) => {
          const userData = userDoc.data();

          users.push({
            id: userDoc.id,
            ...userData,
            name: getEmployeeName(userData),
            email: userData.email || "",
            role: userData.role || "user",
            payroll: userData.payroll || {},
          });
        });

        users.sort((a, b) => (a.name || "").localeCompare(b.name || "", "sv-SE"));
        setEmployees(users);
        setLoading(false);
      },
      (error) => {
        console.error("Fel vid hämtning av anställda:", error);
        setEmployees([]);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [companyRootUid]);

  useEffect(() => {
    if (!companyRootUid) {
      setShifts([]);
      return;
    }

    const shiftsRef = collection(db, "users", companyRootUid, "shifts");

    const unsubscribe = onSnapshot(
      shiftsRef,
      (snapshot) => {
        const shiftList = [];

        snapshot.forEach((shiftDoc) => {
          shiftList.push({
            id: shiftDoc.id,
            ...shiftDoc.data(),
          });
        });

        setShifts(shiftList);
      },
      (error) => {
        console.error("Fel vid hämtning av pass:", error);
        setShifts([]);
      }
    );

    return () => unsubscribe();
  }, [companyRootUid]);

  useEffect(() => {
    if (!companyRootUid) {
      setAbsenceTypes([]);
      return;
    }

    const absenceTypesRef = collection(db, "users", companyRootUid, "absenceTypes");

    const unsubscribe = onSnapshot(
      absenceTypesRef,
      (snapshot) => {
        const types = snapshot.docs.map((docItem) => ({
          id: docItem.id,
          ...docItem.data(),
        }));

        types.sort((a, b) => (a.name || "").localeCompare(b.name || "", "sv-SE"));
        setAbsenceTypes(types);
      },
      (error) => {
        console.error("Fel vid hämtning av frånvarotyper:", error);
        setAbsenceTypes([]);
      }
    );

    return () => unsubscribe();
  }, [companyRootUid]);

  useEffect(() => {
    if (!companyRootUid) {
      setApprovedAbsenceRequests([]);
      return;
    }

    const absenceRequestsRef = collection(
      db,
      "users",
      companyRootUid,
      "absenceRequests"
    );

    const q = query(absenceRequestsRef, where("status", "==", "approved"));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const requests = snapshot.docs.map((docItem) => ({
          id: docItem.id,
          ...docItem.data(),
        }));

        setApprovedAbsenceRequests(requests);
      },
      (error) => {
        console.error("Fel vid hämtning av godkända frånvaroansökningar:", error);
        setApprovedAbsenceRequests([]);
      }
    );

    return () => unsubscribe();
  }, [companyRootUid]);

  const employeeMap = useMemo(() => {
    const map = {};
    employees.forEach((employee) => {
      map[employee.id] = employee;
    });
    return map;
  }, [employees]);

  const employeesWithShifts = useMemo(() => {
    if (!selectedMonth || !selectedYear) return [];

    const selectedMonthIndex = getMonthIndexFromSwedish(selectedMonth);
    const selectedYearNumber = Number(selectedYear);

    const filteredShifts = shifts.filter((shift) => {
      const shiftDate = safeParseDate(shift.date);
      if (!shiftDate) return false;

      return (
        shiftDate.getMonth() === selectedMonthIndex &&
        shiftDate.getFullYear() === selectedYearNumber
      );
    });

    const resultRows = [];

    filteredShifts.forEach((shift) => {
      const employeeRows = [];

      if (Array.isArray(shift.employees) && shift.employees.length > 0) {
        shift.employees.forEach((employeeData) => {
          if (employeeData?.isObemannad) return;
          if (!employeeData?.employeeId) return;

          employeeRows.push({
            employeeId: employeeData.employeeId,
            employeeData,
          });
        });
      } else if (shift.employeeId && !shift.isObemannad) {
        employeeRows.push({
          employeeId: shift.employeeId,
          employeeData: {
            breaks: shift.breaks || [],
            overtime: shift.overtime || [],
            absences: shift.absences || [],
            employeeId: shift.employeeId,
            name: shift.employee || shift.name || "",
          },
        });
      }

      employeeRows.forEach(({ employeeId, employeeData }) => {
        const employeeDataWithApprovedAbsence = mergeApprovedAbsenceForEmployee({
          shift,
          employeeId,
          employeeData: {
            breaks: employeeData?.breaks || [],
            overtime: employeeData?.overtime || [],
            absences: employeeData?.absences || [],
            ...employeeData,
          },
          approvedAbsenceRequests,
          employees,
          absenceTypes,
        });

        const absenceResult = calculateAbsenceForShift(
          shift,
          employeeDataWithApprovedAbsence
        );

        if (absenceResult.hours <= 0) return;

        const employee = employeeMap[employeeId];
        const employeeName =
          employee?.name ||
          employeeDataWithApprovedAbsence?.name ||
          "Okänd anställd";

        const payType = employee?.payroll?.savedPayType || employee?.savedPayType || "hourly";
        const payAmount = employee?.payroll?.savedPayAmount ?? employee?.savedPayAmount ?? 0;
        const absencePay = calculateAbsencePay(
          absenceResult,
          payType,
          payAmount,
          employee
        );
        const absenceValueInSEK = payType === "hourly"
          ? Math.max(0, absenceResult.value) * Math.max(0, Number(payAmount) || 0)
          : absencePay.deduction;
        const workedHours = calculateShiftHours(shift, employeeDataWithApprovedAbsence);

        resultRows.push({
          ...shift,
          employeeId,
          employeeName,
          absenceHours: absenceResult.hours,
          absenceValue: absenceResult.value,
          absenceValueSEK: absenceValueInSEK,
          absenceDetails: absenceResult.details,
          workedHours,
          dateFormatted: formatDateDisplay(shift.date),
          startTimeFormatted: formatTimeDisplay(shift.startTime),
          endTimeFormatted: formatTimeDisplay(shift.endTime),
        });
      });
    });

    const employeeGroupMap = new Map();

    resultRows.forEach((shift) => {
      const employeeId = shift.employeeId || "unknown";
      const employeeName = shift.employeeName || "Okänd anställd";

      if (!employeeGroupMap.has(employeeId)) {
        employeeGroupMap.set(employeeId, {
          id: employeeId,
          name: employeeName,
          shifts: [],
          totalAbsenceHours: 0,
          totalAbsenceValueSEK: 0,
        });
      }

      const employeeData = employeeGroupMap.get(employeeId);
      employeeData.shifts.push(shift);
      employeeData.totalAbsenceHours += Number(shift.absenceHours || 0);
      employeeData.totalAbsenceValueSEK += Number(shift.absenceValueSEK || 0);
    });

    return Array.from(employeeGroupMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name, "sv-SE")
    );
  }, [
    shifts,
    selectedMonth,
    selectedYear,
    employeeMap,
    approvedAbsenceRequests,
    employees,
    absenceTypes,
  ]);

  const filteredEmployees = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();

    return employeesWithShifts.filter((employee) => {
      if (!q) return true;
      return employee.name.toLowerCase().includes(q);
    });
  }, [employeesWithShifts, searchQuery]);

  const summary = useMemo(() => {
    const totalAbsenceHours = filteredEmployees.reduce(
      (sum, employee) => sum + Number(employee.totalAbsenceHours || 0),
      0
    );

    const totalAbsenceValueSEK = filteredEmployees.reduce(
      (sum, employee) => sum + Number(employee.totalAbsenceValueSEK || 0),
      0
    );

    return { totalAbsenceHours, totalAbsenceValueSEK };
  }, [filteredEmployees]);

  const toggleExpand = (employeeId) => {
    setExpandedEmployee(expandedEmployee === employeeId ? null : employeeId);
  };

  const styles = {
    page: {
      minHeight: "100vh",
      background: "#f6f7fb",
      padding: "22px 24px 40px",
      fontFamily:
        'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      color: "#1f2937",
      boxSizing: "border-box",
    },
    container: {
      width: "100%",
      maxWidth: "1400px",
      margin: "0 auto",
    },
    topRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      gap: "16px",
      flexWrap: "wrap",
      marginBottom: "14px",
    },
    title: {
      margin: 0,
      fontSize: "42px",
      lineHeight: 1.1,
      fontWeight: 800,
      color: "#101b3a",
      letterSpacing: "-1px",
      display: "flex",
      alignItems: "center",
      gap: "16px",
    },
    titleIcon: {
      background: "linear-gradient(135deg, #ef4444, #dc2626)",
      padding: "10px",
      borderRadius: "18px",
      color: "#fff",
      display: "inline-flex",
    },
    subtitle: {
      margin: "12px 0 0",
      fontSize: "15px",
      lineHeight: 1.45,
      color: "#5f6b85",
      maxWidth: "640px",
    },
    divider: {
      height: 1,
      background: "#dde3ef",
      margin: "16px 0 18px",
      width: "100%",
    },
    summaryPanel: {
      background: "linear-gradient(135deg, #fef2f2, #fee2e2)",
      borderRadius: "14px",
      padding: "20px",
      marginBottom: "24px",
      border: "1px solid #fecaca",
    },
    summaryTitle: {
      margin: 0,
      fontSize: "20px",
      fontWeight: 700,
      color: "#dc2626",
    },
    summaryMeta: {
      margin: "6px 0 16px",
      fontSize: "14px",
      fontWeight: 600,
      color: "#dc2626",
    },
    summaryGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      gap: "16px",
    },
    summaryCard: {
      background: "#fff",
      border: "1px solid #fecaca",
      borderRadius: "14px",
      padding: "18px 16px",
      display: "flex",
      flexDirection: "column",
      gap: "8px",
    },
    summaryCardLabel: {
      fontSize: "13px",
      fontWeight: 600,
      color: "#6b7280",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    },
    summaryCardValue: {
      fontSize: "28px",
      fontWeight: 800,
      color: "#111827",
    },
    filtersWrap: {
      display: "grid",
      gridTemplateColumns: "1fr 148px 148px",
      gap: "10px",
      background: "#fff",
      borderRadius: "12px",
      padding: "12px",
      marginBottom: "20px",
      border: "1px solid #e5e9f0",
    },
    searchWrap: {
      position: "relative",
      width: "100%",
    },
    searchIcon: {
      position: "absolute",
      left: "14px",
      top: "50%",
      transform: "translateY(-50%)",
      color: "#9ca3af",
      fontSize: "14px",
    },
    searchInput: {
      width: "100%",
      height: "42px",
      borderRadius: "10px",
      border: "1px solid #e2e6ed",
      padding: "0 14px 0 40px",
      fontSize: "14px",
      background: "#fff",
      color: "#111827",
      outline: "none",
      boxSizing: "border-box",
    },
    select: {
      width: "100%",
      height: "42px",
      borderRadius: "10px",
      border: "1px solid #e2e6ed",
      padding: "0 12px",
      fontSize: "14px",
      background: "#fff",
      color: "#111827",
      outline: "none",
      boxSizing: "border-box",
      cursor: "pointer",
    },
    tableWrap: {
      background: "#fff",
      border: "1px solid #e5e9f0",
      borderRadius: "14px",
      overflow: "hidden",
      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      minWidth: "500px",
    },
    thead: {
      background: "#f8fafd",
    },
    th: {
      textAlign: "left",
      fontSize: "13px",
      fontWeight: 700,
      color: "#4a5568",
      padding: "14px 16px",
      borderBottom: "1px solid #e5e9f0",
    },
    td: {
      fontSize: "14px",
      color: "#1a202c",
      padding: "14px 16px",
      borderBottom: "1px solid #edf2f7",
    },
    absenceHoursCell: {
      fontWeight: 600,
      color: "#ef4444",
    },
    absenceValueCell: {
      fontWeight: 600,
      color: "#dc2626",
    },
    emptyState: {
      background: "#fff",
      border: "1px solid #e5e9f0",
      borderRadius: "14px",
      padding: "48px",
      textAlign: "center",
      color: "#6b7280",
    },
    expandRow: {
      cursor: "pointer",
      transition: "background 0.2s",
    },
    detailsRow: {
      background: "#fafcff",
    },
    detailsCell: {
      padding: "20px 24px",
      borderBottom: "1px solid #edf2f7",
    },
    detailsContent: {
      fontSize: "13px",
      color: "#4a5568",
      lineHeight: 1.6,
    },
    shiftItem: {
      padding: "14px",
      background: "#f8fafc",
      borderRadius: "10px",
      marginBottom: "12px",
      border: "1px solid #eef2f6",
    },
    shiftDate: {
      fontWeight: 700,
      color: "#1a202c",
      marginBottom: "8px",
      fontSize: "14px",
    },
    shiftDetails: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
      gap: "12px",
      marginTop: "10px",
    },
    absenceTypeBadge: {
      display: "inline-block",
      padding: "2px 8px",
      borderRadius: "12px",
      fontSize: "11px",
      fontWeight: 600,
      background: "#fee2e2",
      color: "#dc2626",
      marginLeft: "8px",
    },
    requestBadge: {
      display: "inline-block",
      padding: "2px 8px",
      borderRadius: "12px",
      fontSize: "11px",
      fontWeight: 600,
      background: "#dcfce7",
      color: "#166534",
      marginLeft: "8px",
    },
  };

  if (companyLoading || loading || !selectedMonth || !selectedYear) {
    return <LoadingIndicator message="Laddar frånvarodata..." fullPage />;
  }

  if (!activeCompany) {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <div style={styles.emptyState}>
            Inget företag valt. Vänligen logga in igen.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <style>{absenceOverviewCss}</style>
      <div style={styles.container}>
        <div style={styles.topRow}>
          <div>
            <h1 style={styles.title}>
              <span style={styles.titleIcon}>
                <FaUserSlash size={28} />
              </span>
              Frånvaro-översikt
            </h1>
            <p style={styles.subtitle}>
              Översikt över frånvaro för vald period för{" "}
              <strong>{activeCompany?.companyName || "företaget"}</strong>.
              Klicka på en anställd för att se alla frånvaropass under perioden.
            </p>
          </div>
        </div>

        <div style={styles.divider} />

        <div style={styles.summaryPanel}>
          <h2 style={styles.summaryTitle}>SAMMANFATTNING</h2>
          <div style={styles.summaryMeta}>
            {selectedMonth.toUpperCase()} {selectedYear}
          </div>

          <div style={styles.summaryGrid}>
            <div style={styles.summaryCard}>
              <div style={styles.summaryCardLabel}>
                <FaRegClock style={{ marginRight: "6px" }} />
                Frånvarotimmar
              </div>
              <div style={styles.summaryCardValue}>
                {formatHours(summary.totalAbsenceHours)} h
              </div>
            </div>

            <div style={styles.summaryCard}>
              <div style={styles.summaryCardLabel}>
                <FaMoneyBillWave style={{ marginRight: "6px" }} />
                Frånvarokostnad (avdrag)
              </div>
              <div style={styles.summaryCardValue}>
                {summary.totalAbsenceValueSEK > 0
                  ? `−${formatCurrency(summary.totalAbsenceValueSEK)}`
                  : formatCurrency(0)}
              </div>
            </div>
          </div>
        </div>

        <div style={styles.filtersWrap}>
          <div style={styles.searchWrap}>
            <FaSearch style={styles.searchIcon} />
            <input
              type="text"
              placeholder="Sök efter anställd..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={styles.searchInput}
            />
          </div>

          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            style={styles.select}
          >
            {monthsInSwedish.map((month) => (
              <option key={month} value={month}>
                {month}
              </option>
            ))}
          </select>

          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            style={styles.select}
          >
            {years.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>

        {filteredEmployees.length === 0 ? (
          <div style={styles.emptyState}>
            <FaRegClock size={32} style={{ marginBottom: "12px", color: "#9ca3af" }} />
            <div>Ingen frånvaro hittades för vald period.</div>
            <div style={{ fontSize: "12px", marginTop: "8px", color: "#9ca3af" }}>
              Kontrollera att det finns registrerade eller godkända frånvaropass
              för denna period.
            </div>
          </div>
        ) : (
          <div style={styles.tableWrap}>
            <div style={{ overflowX: "auto" }}>
              <table style={styles.table}>
                <thead style={styles.thead}>
                  <tr>
                    <th style={styles.th}>Namn</th>
                    <th style={styles.th}>Frånvarotimmar</th>
                    <th style={styles.th}>Frånvarokostnad (avdrag)</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredEmployees.map((employee) => (
                    <React.Fragment key={employee.id}>
                      <tr
                        style={styles.expandRow}
                        onClick={() => toggleExpand(employee.id)}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.background = "#f8f9fc")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.background = "transparent")
                        }
                      >
                        <td style={styles.td}>
                          <button type="button" className="absence-person-toggle" aria-expanded={expandedEmployee === employee.id} onClick={(event) => { event.stopPropagation(); toggleExpand(employee.id); }}>
                            <FaUser size={14} color="#6b7280" />
                            {employee.name}
                            <span aria-hidden="true" style={{ marginLeft: "auto" }}>{expandedEmployee === employee.id ? "▴" : "▾"}</span>
                          </button>
                        </td>
                        <td style={{ ...styles.td, ...styles.absenceHoursCell }}>
                          {employee.totalAbsenceHours > 0
                            ? formatHours(employee.totalAbsenceHours)
                            : "-"}
                        </td>
                        <td style={{ ...styles.td, ...styles.absenceValueCell }}>
                          {employee.totalAbsenceValueSEK > 0
                            ? `−${formatCurrency(employee.totalAbsenceValueSEK)}`
                            : formatCurrency(0)}
                        </td>
                      </tr>

                      {expandedEmployee === employee.id && (
                        <tr style={styles.detailsRow}>
                          <td colSpan="3" style={styles.detailsCell}>
                            <div style={styles.detailsContent}>
                              <div className="absence-person-heading">
                                <strong>Frånvaro för {employee.name}</strong>
                                <span>{selectedMonth} {selectedYear} · {employee.shifts.length} pass</span>
                              </div>
                              <p className="absence-help">Öppna ”Visa frånvarodetaljer” för att se typ av frånvaro, tider och hur stor del av lönen som behålls.</p>
                              <div className="absence-shift-list">
                                {[...employee.shifts].sort((a, b) =>
                                  (safeParseDate(a.date)?.getTime() || 0) - (safeParseDate(b.date)?.getTime() || 0) ||
                                  String(a.startTime).localeCompare(String(b.startTime))
                                ).map((shift, index) => (
                                  <article key={`${shift.id}-${index}`} className="absence-shift-card">
                                    <div className="absence-shift-summary">
                                      <div><strong>{shift.dateFormatted}</strong><span className="absence-muted">Pass {shift.startTimeFormatted}–{shift.endTimeFormatted}</span></div>
                                      <div><span className="absence-muted">Arbetad tid</span><span>{formatHours(shift.workedHours)} h</span></div>
                                      <div><span className="absence-muted">Frånvaro</span><span>{formatHours(shift.absenceHours)} h</span></div>
                                      <div className="absence-shift-amount"><span className="absence-muted">Löneavdrag</span><strong>{shift.absenceValueSEK > 0 ? `−${formatCurrency(shift.absenceValueSEK)}` : formatCurrency(shift.absenceValueSEK)}</strong></div>
                                    </div>
                                    {(shift.absenceDetails?.length > 0 || shift.notes) && (
                                      <details className="absence-calculation">
                                        <summary><span className="absence-show">Visa frånvarodetaljer</span><span className="absence-hide">Dölj frånvarodetaljer</span></summary>
                                        {shift.absenceDetails?.length > 0 && (
                                          <div className="absence-calculation-scroll">
                                            <table className="absence-calculation-table">
                                              <caption>Frånvaro för passet {shift.dateFormatted}</caption>
                                              <thead><tr><th scope="col">Typ av frånvaro</th><th scope="col">Frånvarotid</th><th scope="col">Antal timmar</th><th scope="col">Lön som behålls</th></tr></thead>
                                              <tbody>
                                                {shift.absenceDetails.map((detail, idx) => (
                                                  <tr key={idx}>
                                                    <th scope="row">{detail.type || "Frånvaro"}{detail.approvedFromRequest && <span className="absence-muted">Godkänd ansökan</span>}</th>
                                                    <td>{detail.appliesToWholeShift ? "Hela passet" : `${detail.startTime}–${detail.endTime}`}</td>
                                                    <td>{formatHours(detail.hours)} h</td>
                                                    <td>{detail.absencePercent}%</td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </table>
                                          </div>
                                        )}
                                        <p className="absence-help">Beloppet visar värdet av obetald frånvaro. Vid timlön är de timmarna redan borttagna från arbetad tid. Vid månadslön dras beloppet från månadslönen.</p>
                                        {shift.notes && <p className="absence-help"><strong>Anteckning: </strong>{shift.notes}</p>}
                                      </details>
                                    )}
                                  </article>
                                ))}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
